import Ssl from "@/classes/ssl";
import PopupShell from "@/components/popup-shell";
import globals from "@/globals";
import { closePopup, showDialog } from "@/utils";
import { DialogTitle } from "@headlessui/react";
import { t } from "i18next";
import { JSX, useCallback, useId, useRef, useState } from "react";

const SIZE_LIMIT = 1048576;

function SslPopup(): JSX.Element {
	const [isClosing, setIsClosing] = useState(false);

	const certificateRef = useRef<HTMLInputElement>(null);
	const privateKeyRef = useRef<HTMLInputElement>(null);

	const certificateId = useId();
	const privateKeyId = useId();

	const closeSslPopup = useCallback((): void => {
		setIsClosing(true);
		window.setTimeout(() => {
			closePopup("ssl");
			setIsClosing(false);
		}, globals.ANIMATION_WAIT_TIME);
	}, []);

	const uploadSsl = useCallback((): void => {
		const certInput = certificateRef.current;
		const keyInput = privateKeyRef.current;
		if (!certInput?.files || !keyInput?.files) {
			return;
		}
		const cert = certInput.files[0];
		const key = keyInput.files[0];
		const ssl = new Ssl();
		closeSslPopup();
		if (!cert || !key) {
			void ssl.deleteCertificate();
			return;
		}
		if (cert.size > SIZE_LIMIT || key.size > SIZE_LIMIT) {
			void showDialog(t("certificateInvalid"));
			return;
		}
		void ssl.uploadCertificate(cert, key);
	}, [closeSslPopup]);

	return (
		<PopupShell
			onClose={closeSslPopup}
			isClosing={isClosing}
			isOpen
		>
			<DialogTitle as="h1">{t("uploadSslCertificate")}</DialogTitle>
			<label htmlFor={certificateId}>
				{t("certificate")}
				<a
					className="link"
					href="https://cloud.tencent.com/product/ssl"
					target="_blank"
					rel="noopener noreferrer"
				>
					{t("applyForFree")}
				</a>
			</label>
			<div className="description">{t("certificateTip")}</div>
			<input
				className="input-box"
				id={certificateId}
				ref={certificateRef}
				type="file"
			/>
			<label htmlFor={privateKeyId}>{t("privateKey")}</label>
			<div className="description">{t("privateKeyTip")}</div>
			<input
				id={privateKeyId}
				ref={privateKeyRef}
				className="input-box"
				type="file"
			/>
			<div className="button-container">
				<button
					className="button"
					type="button"
					onClick={closeSslPopup}
				>
					{t("cancel")}
				</button>
				<button
					className="default-button"
					type="button"
					onClick={uploadSsl}
				>
					{t("upload")}
				</button>
			</div>
		</PopupShell>
	);
}

export default SslPopup;
